import React, { useEffect, useMemo, useState } from 'react';
import { invoke } from '@tauri-apps/api/core';
import {
  ArrowUp,
  ArrowDown,
  X,
  Check,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  RotateCcw,
  AlertCircle,
  Folder,
} from 'lucide-react';
import OsScroll from '../ui/OsScroll';
import Tooltip from '../ui/Tooltip';
import TruncatedText from '../ui/TruncatedText';
import { formatBytes } from '../../utils/formatBytes';
import { formatRate, formatEta, etaSeconds } from '../../utils/transferRate';
import { useFtpStore } from '../../store/ftpStore';
import {
  transferStatus,
  groupTransfers,
  overallProgress,
  sizeLabel,
  isFinished,
} from '../../utils/transferGroups';
import { canRetry, runRetry, pruneRetries } from './transferRetry';

/**
 * The FTP/SFTP transfers strip: one LINE per transfer, a folder upload as one
 * collapsible group with its totals, and a header with the overall progress.
 *
 * Every row is the same grid — direction · name · bar · size · status · action —
 * with the NAME as the only flexible column, so a narrow panel truncates the
 * name first and nothing ever wraps (the owner's "12.4 KB on two rows").
 * Moved out of FtpView.js, which is a megafile (owner rule: small focused files).
 */

/** Stop one row: a queued one never started, so it is just marked; a running
 *  one is cancelled in Rust, which reports back through the progress feed. */
export function cancelTransferRow(tr) {
  const status = transferStatus(tr);
  if (status === 'queued') {
    useFtpStore.getState().upsertTransfer({ transferId: tr.transferId, done: true, canceled: true });
  } else if (status === 'running') {
    invoke('ftp_transfer_cancel', { transferId: tr.transferId }).catch(() => {});
  }
}

function retryRow(tr) {
  useFtpStore.getState().removeTransfer(tr.transferId);
  runRetry(tr.transferId);
}

function Bar({ percent, indeterminate, ok }) {
  return (
    <div className={`ftp-progress${indeterminate ? ' ftp-progress--indeterminate' : ''}`}>
      <div
        className="ftp-progress-fill"
        style={ok ? { width: '100%' } : percent != null ? { width: `${percent}%` } : undefined}
      />
    </div>
  );
}

function StatusCell({ tr, t }) {
  const status = transferStatus(tr);
  if (status === 'running') {
    // Rate and ETA only while running: on a finished row they are trivia, and
    // on a stalled one the ABSENT rate is the signal ("0 B/s" would bury it).
    const rate = formatRate(tr.bps, formatBytes);
    const eta = formatEta(etaSeconds(tr.transferred, tr.total, tr.bps));
    return <span className="ftp-transfer-status">{rate && eta ? `${rate} · ${eta}` : rate || ''}</span>;
  }
  if (status === 'failed') {
    return (
      <Tooltip content={tr.error} side="top">
        <span
          className="ftp-transfer-status ftp-transfer-status--failed"
          data-status="failed"
          // The Tooltip would otherwise make the bare error the accessible name.
          aria-label={`${t('ftp.transferStatusFailed')}: ${tr.error}`}
        >
          <AlertCircle size={11} />
          {t('ftp.transferStatusFailed')}
        </span>
      </Tooltip>
    );
  }
  const label = {
    queued: t('ftp.transferStatusQueued'),
    done: t('ftp.transferStatusDone'),
    replaced: t('ftp.transferStatusReplaced'),
    canceled: t('ftp.transferCanceled'),
    skipped: t('ftp.transferStatusSkipped'),
  }[status];
  return (
    <span className={`ftp-transfer-status ftp-transfer-status--${status}`} data-status={status}>
      {(status === 'done' || status === 'replaced') && <Check size={11} />}
      {label}
    </span>
  );
}

function RowAction({ tr, t }) {
  const status = transferStatus(tr);
  if (status === 'queued' || status === 'running') {
    return (
      <Tooltip content={t('ftp.cancelTransfer')} side="top">
        <button
          type="button"
          className="ftp-transfer-btn"
          aria-label={t('ftp.cancelTransfer')}
          onClick={() => cancelTransferRow(tr)}
        >
          <X size={12} />
        </button>
      </Tooltip>
    );
  }
  if ((status === 'failed' || status === 'canceled') && canRetry(tr.transferId)) {
    return (
      <Tooltip content={t('ftp.retryTransfer')} side="top">
        <button
          type="button"
          className="ftp-transfer-btn ftp-transfer-btn--retry"
          aria-label={t('ftp.retryTransfer')}
          onClick={() => retryRow(tr)}
        >
          <RotateCcw size={12} />
        </button>
      </Tooltip>
    );
  }
  return (
    <Tooltip content={t('ftp.clearFinished')} side="top">
      <button
        type="button"
        className="ftp-transfer-btn"
        aria-label={t('ftp.clearFinished')}
        onClick={() => useFtpStore.getState().removeTransfer(tr.transferId)}
      >
        <X size={12} />
      </button>
    </Tooltip>
  );
}

export function TransferRow({ tr, t, nested = false }) {
  const status = transferStatus(tr);
  const pct = tr.total > 0 ? Math.min(100, Math.round((tr.transferred / tr.total) * 100)) : null;
  const ok = status === 'done' || status === 'replaced';
  return (
    <div className={`ftp-transfer-row${nested ? ' ftp-transfer-row--nested' : ''}`} data-status={status}>
      <span className={`ftp-transfer-dir ftp-transfer-dir--${tr.kind}`}>
        {tr.kind === 'upload' ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
      </span>
      {tr.path && tr.path !== tr.name ? (
        // The full destination path, always: inside a folder upload the name
        // alone ("index.js") does not say WHICH index.js.
        <Tooltip content={tr.path} side="top">
          <span className="ftp-transfer-name">{tr.name}</span>
        </Tooltip>
      ) : (
        <TruncatedText className="ftp-transfer-name" text={tr.name} />
      )}
      <div className="ftp-transfer-mid">
        {status === 'skipped' || status === 'canceled' || status === 'failed' ? null : (
          <Bar percent={pct} indeterminate={pct == null && status === 'running'} ok={ok} />
        )}
      </div>
      <span className="ftp-transfer-size">{sizeLabel(tr, formatBytes)}</span>
      <StatusCell tr={tr} t={t} />
      <RowAction tr={tr} t={t} />
    </div>
  );
}

function TransferGroup({ group, t }) {
  const [open, setOpen] = useState(false);
  const s = group.summary;
  const running = s.active > 0;
  const status = running ? 'running' : s.failed > 0 ? 'failed' : s.canceled > 0 && s.finishedOk === 0 ? 'canceled' : 'done';
  const size =
    s.total > 0 ? (running ? `${formatBytes(s.transferred)} / ${formatBytes(s.total)}` : formatBytes(s.total)) : '';
  const extras = [
    s.skipped > 0 ? t('ftp.groupSkipped', { count: s.skipped }) : null,
    s.failed > 0 ? t('ftp.groupFailed', { count: s.failed }) : null,
  ].filter(Boolean);
  const filesLabel = t('ftp.groupFiles', { done: s.finishedOk, count: s.files });
  const toggleLabel = open ? t('ftp.collapseGroup') : t('ftp.expandGroup');
  return (
    <div className="ftp-transfer-group" data-status={status}>
      <div className="ftp-transfer-row ftp-transfer-row--group">
        <Tooltip content={toggleLabel} side="top">
          <button
            type="button"
            className="ftp-transfer-btn ftp-transfer-group-toggle"
            aria-label={toggleLabel}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </button>
        </Tooltip>
        <Tooltip content={group.path || group.name} side="top">
          <span className="ftp-transfer-name ftp-transfer-name--group">
            <Folder size={12} className="ftp-transfer-group-icon" />
            <span className="ftp-transfer-group-name">{group.name}</span>
          </span>
        </Tooltip>
        <div className="ftp-transfer-mid">
          <Bar percent={s.percent} indeterminate={running && s.percent == null} ok={status === 'done'} />
        </div>
        <span className="ftp-transfer-size">{size}</span>
        <Tooltip content={extras.join(' · ')} side="top" disabled={extras.length === 0}>
          <span
            className={`ftp-transfer-status ftp-transfer-status--${status}`}
            data-status={status}
            aria-label={[filesLabel, ...extras].join(' · ')}
          >
            {status === 'failed' && <AlertCircle size={11} />}
            {filesLabel}
          </span>
        </Tooltip>
        {running ? (
          <Tooltip content={t('ftp.cancelGroup')} side="top">
            <button
              type="button"
              className="ftp-transfer-btn"
              aria-label={t('ftp.cancelGroup')}
              onClick={() => group.rows.forEach(cancelTransferRow)}
            >
              <X size={12} />
            </button>
          </Tooltip>
        ) : (
          <Tooltip content={t('ftp.clearFinished')} side="top">
            <button
              type="button"
              className="ftp-transfer-btn"
              aria-label={t('ftp.clearFinished')}
              onClick={() => group.rows.forEach((tr) => useFtpStore.getState().removeTransfer(tr.transferId))}
            >
              <X size={12} />
            </button>
          </Tooltip>
        )}
      </div>
      {open && group.rows.map((tr) => <TransferRow key={tr.transferId} tr={tr} t={t} nested />)}
    </div>
  );
}

export default function TransfersStrip({ t }) {
  const transfers = useFtpStore((s) => s.transfers);
  const [collapsed, setCollapsed] = useState(false);
  const items = useMemo(() => groupTransfers(transfers), [transfers]);
  const overall = useMemo(() => overallProgress(transfers), [transfers]);

  useEffect(() => {
    pruneRetries(transfers.map((tr) => tr.transferId));
  }, [transfers]);

  if (transfers.length === 0) return null;
  const hasFinished = transfers.some(isFinished);
  return (
    <div className="ftp-transfers">
      <div className="ftp-transfers-header">
        <button type="button" className="ftp-transfers-toggle" onClick={() => setCollapsed((c) => !c)}>
          {collapsed ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          <span>{t('ftp.transfers')}</span>
          {overall.active > 0 && <span className="ftp-transfers-count">{overall.active}</span>}
        </button>
        {overall.active > 0 && (
          <div className="ftp-transfers-overall" data-testid="ftp-transfers-overall">
            <Bar percent={overall.percent} indeterminate={overall.percent == null} />
            <span className="ftp-transfers-overall-pct">{overall.percent != null ? `${overall.percent}%` : ''}</span>
          </div>
        )}
        {hasFinished && (
          <button
            type="button"
            className="ftp-mini-btn"
            onClick={() => useFtpStore.getState().clearFinishedTransfers()}
          >
            {t('ftp.clearFinishedAll')}
          </button>
        )}
      </div>
      {!collapsed && (
        <OsScroll style={{ maxHeight: 160 }}>
          <div className="ftp-transfer-list">
            {items.map((item) =>
              item.type === 'group' ? (
                <TransferGroup key={`g:${item.id}`} group={item} t={t} />
              ) : (
                <TransferRow key={item.tr.transferId} tr={item.tr} t={t} />
              )
            )}
          </div>
        </OsScroll>
      )}
    </div>
  );
}
